import { padding } from 'polished'

export const __overlay = ({ theme, overlayColor }) => ({
	width: '100%',
	height: '100%',
	background: overlayColor || theme.colors.darkGrey4,
	opacity: 0.5,
	position: 'fixed',
	top: 0,
	left: 0,
	zIndex: 1000,
})

export const __container = ({
	verticalPosition,
	popupMargin = 0,
	popupRadius = 0,
	theme,
}) => ({
	position: 'fixed',
	padding: 16,
	left: '50%',
	transform: 'translateX(-50%)',
	flexDirection: 'column',
	width: `calc(100% - ${popupMargin}px)`,
	borderRadius: popupRadius,
	maxWidth: 480,
	zIndex: 1001,
	background: theme.colors.white,
	extend: [
		{
			condition: verticalPosition === 'top',
			style: {
				top: 0,
			},
		},
		{
			condition: verticalPosition === 'middle',
			style: {
				top: '50%',
				transform: 'translateX(-50%) translateY(-50%)',
			},
		},
		{
			condition: verticalPosition === 'bottom',
			style: {
				bottom: '0',
			},
		},
	],
})

export const __header = ({ theme }) => ({
	...theme.mediumBaseFont,
	fontSize: theme.font.size16,
	display: 'flex',
	color: theme.colors.darkGrey4,
	padding: `${theme.spaces[8]} 0 ${theme.spaces[8]}`,
	// borderBottom: `1px solid ${theme.colors.lightGrey3}`,
	...padding(0, theme.spaces[16]),
	justifyContent: 'space-between',
	alignItems: 'center',
	'& div': {
		textAlign: 'center',
		flex: 1,
		margin: 0,
	},
})

// Added new subtitle component to display part of the message in bold
export const __subtitle = ({ theme }) => ({
	fontSize: theme.font.size16,
	fontWeight: 400,
	color: theme.colors.darkGrey4,
	display: 'flex',
	alignContent: 'middle',
	...theme.mediumBaseFont,
	textAlign: 'center',
	justifyContent: 'center',
	paddingTop: theme.spaces[16],
	'& a:link': {
		color: theme.primary(theme).backgroundColor,
		textAlign: 'center',
		textDecoration: 'none',
	},
	'& a:visited': {
		color: theme.primary(theme).backgroundColor,
		textAlign: 'center',
		textDecoration: 'none',
	},
})

export const __content = ({ theme, contentstart }) => ({
	fontSize: theme.font.size16,
	color: theme.colors.darkGrey4,
	display: 'flex',
	alignContent: 'middle',
	fontWeight: 'none',
	textAlign: 'center',
	marginBottom: '24px',
	justifyContent: contentstart ? 'flex-start' : 'center',
	// paddingTop: theme.spaces[16],
	'& a:link': {
		color: theme.primary(theme).backgroundColor,
		textAlign: 'center',
		textDecoration: 'none',
	},
	'& a:visited': {
		color: theme.primary(theme).backgroundColor,
		textAlign: 'center',
		textDecoration: 'none',
	},
})

export const __footer = ({ theme }) => ({
	display: 'flex',
	flexDirection: 'row',
	color: theme.colors.darkGrey4,
	textAlign: 'center',
	justifyContent: 'center',
	marginBottom: '8px',
	'& button:not(:last-child)': {
		marginRight: 10,
	},
	'& button': {
		marginRight: theme.spaces[8],
	},
})

export const __button = ({
	theme,
	textColor,
	backgroundColor,
	inverted = false,
}) => ({
	...theme.mediumBaseFont,
	width: '100%',
	border: inverted ? `2px solid ${textColor}` : 'none',
	borderRadius: 0,
	textAlign: 'center',
	background: inverted ? theme.colors.white : backgroundColor,
	color: textColor,
	...padding(15, 0),
})

export const __iconWrapper = () => ({
	width: 15,
	height: 15,
	display: 'flex',
	flex: 0,
	position: 'absolute',
	top: 19,
	right: 18,
})

const __moveUpKeyframe = ({ verticalPosition }) => {
	const transform = []

	switch (verticalPosition) {
		case 'middle':
			transform.push('translateX(-50%) translateY(-40%)')
			transform.push('translateX(-50%) translateY(-50%)')
			break
		case 'top':
		case 'bottom':
			transform.push('translateX(-50%) translateY(10%)')
			transform.push('translateX(-50%) translateY(0)')
			break
		default:
			break
	}

	return {
		'0%': {
			opacity: '0',
			transform: transform[0],
		},
		'100%': {
			opacity: '1',
			transform: transform[1],
		},
	}
}

const __moveDownKeyframe = ({ verticalPosition }) => {
	const transform = []

	switch (verticalPosition) {
		case 'middle':
			transform.push('translateX(-50%) translateY(-50%) scale(1)')
			transform.push('translateX(-50%) translateY(-40%) scale(1)')
			transform.push('translateX(-50%) translateY(-40%) scale(0)')
			break
		case 'top':
		case 'bottom':
			transform.push('translateX(-50%) translateY(0) scale(1)')
			transform.push('translateX(-50%) translateY(10%) scale(1)')
			transform.push('translateX(-50%) translateY(10%) scale(0)')
			break
		default:
			break
	}

	return {
		'0%': {
			opacity: '1',
			transform: transform[0],
		},
		'99%': {
			opacity: '0',
			transform: transform[1],
		},
		'100%': {
			opacity: '0',
			transform: transform[2],
		},
	}
}

const __fadeInKeyframe = () => ({
	'0%': { opacity: '0' },
	'100%': { opacity: '1' },
})

const __fadeOutKeyframe = () => ({
	'0%': { opacity: '1', transform: 'scale(1)' },
	'99%': { opacity: '0', transform: 'scale(1)' },
	'100%': { opacity: '0', transform: 'scale(0)' },
})

export const __moveUp = ({ verticalPosition }) => ({
	animationName: __moveUpKeyframe({ verticalPosition }),
	animationDuration: '0.2s',
	animationTimingFunction: 'linear',
	animationFillMode: 'forwards',
})

export const __moveDown = ({ verticalPosition }) => ({
	animationName: __moveDownKeyframe({ verticalPosition }),
	animationDuration: '0.2s',
	animationTimingFunction: 'linear',
	animationFillMode: 'forwards',
})

export const __fadeIn = () => ({
	animationName: __fadeInKeyframe(),
	animationDuration: '0.2s',
	animationTimingFunction: 'linear',
	animationFillMode: 'forwards',
})

export const __fadeOut = () => ({
	animationName: __fadeOutKeyframe(),
	animationDuration: '0.2s',
	animationTimingFunction: 'linear',
	animationFillMode: 'forwards',
})

export const __statusIcon = ({ theme }) => ({
	width: theme.spaces[56],
	margin: `${theme.spaces[8]} auto ${theme.spaces[16]}`,
})
